/**
 * One way to turn a failed API response into an Error (EP-2 / EP-44).
 *
 * FastAPI's `detail` arrives in three shapes:
 *  - a string;
 *  - an array of Pydantic validation errors ({loc, msg, type}) on a 422;
 *  - an object from the AI layer: {available: false, usage, reason, attempts?}
 *    on a 503 (no model can serve the usage) or {available: false, usage,
 *    reason, ai_provenance?} on a 502 (the model's reply was unusable).
 *
 * The Error's message is always readable, and AI failures are flagged so a
 * component can say "AI unavailable" honestly instead of showing a stand-in.
 */

export function formatErrorDetail(detail) {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (d && typeof d === 'object' ? d.msg || JSON.stringify(d) : String(d)))
      .join('; ');
  }
  if (detail && typeof detail === 'object') {
    if (typeof detail.reason === 'string' && detail.reason) return detail.reason;
    if (typeof detail.message === 'string' && detail.message) return detail.message;
  }
  return null;
}

export function apiError(status, statusText, body) {
  const detail = body && typeof body === 'object' ? body.detail : undefined;
  const error = new Error(formatErrorDetail(detail) || `HTTP ${status}: ${statusText}`);
  error.status = status;
  error.detail = detail;
  const aiDetail = detail && typeof detail === 'object' && !Array.isArray(detail) && detail.available === false;
  error.aiUnavailable = Boolean(aiDetail && status === 503);
  error.aiOutputInvalid = Boolean(aiDetail && status === 502);
  return error;
}

export async function apiErrorFromResponse(response) {
  const body = await response.json().catch(() => null);
  return apiError(response.status, response.statusText, body);
}
